import type { HttpCore } from '../http';
import { ApiError } from '../errors';

/**
 * Authenticated learner-side HITL API. Types/behavior/truncation limits
 * mirror `frontend/web-ide/src/services/hitl.ts` exactly.
 *
 * Identity never crosses this boundary in a request body — the server
 * resolves the learner and teaching-session id from the bearer token.
 */

export const HITL_NOTE_MAX_LENGTH = 1_000;
export const HITL_MESSAGE_MAX_LENGTH = 4_000;
export const HITL_NOTE_MAX_BYTES = 2_000;
export const HITL_MESSAGE_MAX_BYTES = 8_000;
export const HITL_SOURCE_CONTENT_ID_MAX_LENGTH = 128;
export const HITL_SNAPSHOT_PATH_MAX_LENGTH = 512;
export const HITL_SNAPSHOT_CONTENT_MAX_LENGTH = 50_000;
export const HITL_TERMINAL_TAIL_MAX_LENGTH = 20_000;

export type EscalationSeverity = 'low' | 'medium' | 'high';
export type EscalationStatus = 'waiting' | 'assigned' | 'resolved' | 'cancelled';
export type HitlSessionStatus = 'active' | 'ended' | 'abandoned';

export interface Escalation {
  id: string;
  learner_id: string;
  source_lecture_id: string | null;
  source_ai_conversation_id: string | null;
  severity: EscalationSeverity;
  status: EscalationStatus;
  requested_at: string;
  wait_time_ms: number | null;
  resolved_at: string | null;
  learner_note: string | null;
  session_id?: string | null;
  session_status?: HitlSessionStatus | null;
  receipt_acknowledged_at?: string | null;
  receipt_pending?: boolean;
}

export interface CodeSnapshot {
  file_path?: string;
  content?: string;
  terminal_tail?: string;
}

export interface EscalationRequest {
  source_teaching_session_id: string;
  source_lecture_id?: string;
  code_snapshot?: CodeSnapshot;
  learner_note?: string;
  severity?: EscalationSeverity;
}

export interface EscalationStatusResp {
  id: string;
  status: EscalationStatus;
  severity: EscalationSeverity;
  requested_at: string;
  wait_time_ms: number | null;
  session_id: string | null;
  session_status: HitlSessionStatus | null;
}

export interface HitlMessage {
  id: string;
  session_id: string;
  sender_type: 'learner' | 'instructor' | 'ai';
  content: string;
  sent_at: string;
  sequence: number;
  client_message_id?: string | null;
}

export interface Annotation {
  id: string;
  session_id: string;
  line_range_start: number;
  line_range_end: number;
  note: string;
  file_path: string | null;
  created_at: string;
}

export interface HitlSession {
  id: string;
  escalation_id: string;
  instructor_id: string;
  taken_at: string;
  handback_at: string | null;
  session_length_ms: number | null;
  status: HitlSessionStatus;
}

export interface Handback {
  session_id: string;
  covered_chips: { slug: string; label: string }[];
  watch_for_note: string | null;
  learner_confidence_rating: number | null;
  created_at: string;
}

export interface HitlSnapshot {
  session: HitlSession;
  escalation: Escalation;
  messages: HitlMessage[];
  annotations: Annotation[];
}

export interface Receipt extends HitlSnapshot {
  handback: Handback | null;
}

export interface ReceiptAcknowledgement {
  acknowledged: true;
  session_id: string;
  receipt_acknowledged_at: string;
}

function utf8BytesForCodePoint(value: string): number {
  const codePoint = value.codePointAt(0) ?? 0;
  if (codePoint <= 0x7f) return 1;
  if (codePoint <= 0x7ff) return 2;
  if (codePoint <= 0xffff) return 3;
  return 4;
}

export function truncateUtf8(value: string, maxBytes: number, keep: 'start' | 'end' = 'start'): string {
  if (maxBytes <= 0 || value.length === 0) return '';

  let bytes = 0;
  const characters: string[] = [];
  if (keep === 'start') {
    for (const character of Array.from(value)) {
      const nextBytes = utf8BytesForCodePoint(character);
      if (bytes + nextBytes > maxBytes) break;
      bytes += nextBytes;
      characters.push(character);
    }
    return characters.join('');
  }

  let index = value.length;
  while (index > 0) {
    let start = index - 1;
    const trailing = value.charCodeAt(start);
    if (trailing >= 0xdc00 && trailing <= 0xdfff && start > 0) {
      const leading = value.charCodeAt(start - 1);
      if (leading >= 0xd800 && leading <= 0xdbff) start -= 1;
    }
    const character = value.slice(start, index);
    const nextBytes = utf8BytesForCodePoint(character);
    if (bytes + nextBytes > maxBytes) break;
    bytes += nextBytes;
    characters.push(character);
    index = start;
  }
  return characters.reverse().join('');
}

export function truncateCodePoints(value: string, maxCharacters: number): string {
  if (maxCharacters <= 0 || value.length === 0) return '';
  const characters: string[] = [];
  for (const character of Array.from(value)) {
    if (characters.length >= maxCharacters) break;
    characters.push(character);
  }
  return characters.join('');
}

export function boundCodeSnapshot(snapshot?: CodeSnapshot): CodeSnapshot | undefined {
  if (!snapshot) return undefined;
  const bounded: CodeSnapshot = {};
  if (typeof snapshot.file_path === 'string') {
    bounded.file_path = truncateUtf8(snapshot.file_path, HITL_SNAPSHOT_PATH_MAX_LENGTH);
  }
  if (typeof snapshot.content === 'string') {
    bounded.content = truncateUtf8(snapshot.content, HITL_SNAPSHOT_CONTENT_MAX_LENGTH);
  }
  if (typeof snapshot.terminal_tail === 'string') {
    bounded.terminal_tail = truncateUtf8(snapshot.terminal_tail, HITL_TERMINAL_TAIL_MAX_LENGTH, 'end');
  }
  return Object.keys(bounded).length > 0 ? bounded : undefined;
}

export function createClientMessageId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  if (bytes.every((value) => value === 0)) {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
  return [hex.slice(0, 4).join(''), hex.slice(4, 6).join(''), hex.slice(6, 8).join(''), hex.slice(8, 10).join(''), hex.slice(10).join('')].join('-');
}

export function isHitlUnavailable(error: unknown): boolean {
  return error instanceof ApiError && (error.body as { error?: unknown } | undefined)?.error === 'hitl_unavailable';
}

export function createHitlModule(core: HttpCore) {
  const api = {
    async request(input: EscalationRequest): Promise<Escalation> {
      const res = await core.request<{ escalation: Escalation }>('/hitl/escalations/request', {
        method: 'POST',
        body: {
          ...input,
          source_lecture_id: input.source_lecture_id
            ? truncateUtf8(input.source_lecture_id.trim(), HITL_SOURCE_CONTENT_ID_MAX_LENGTH) || undefined
            : undefined,
          learner_note: input.learner_note
            ? truncateUtf8(truncateCodePoints(input.learner_note.trim(), HITL_NOTE_MAX_LENGTH), HITL_NOTE_MAX_BYTES) ||
              undefined
            : undefined,
          code_snapshot: boundCodeSnapshot(input.code_snapshot),
        },
      });
      return res.escalation;
    },

    async requestOrRecover(input: EscalationRequest): Promise<Escalation> {
      try {
        return await api.request(input);
      } catch (requestError) {
        try {
          const current = await api.getCurrent();
          if (current) return current;
        } catch {
          // Preserve the actionable request error when recovery is unavailable.
        }
        throw requestError;
      }
    },

    async getCurrent(): Promise<Escalation | null> {
      const res = await core.request<{ escalation: Escalation | null }>('/hitl/escalations/current');
      return res.escalation ?? null;
    },

    async getStatus(escalationId: string): Promise<EscalationStatusResp> {
      return core.request<EscalationStatusResp>(`/hitl/escalations/${escalationId}/status`);
    },

    async cancel(escalationId: string): Promise<Escalation> {
      const res = await core.request<{ escalation: Escalation }>(`/hitl/escalations/${escalationId}/cancel`, {
        method: 'POST',
      });
      return res.escalation;
    },

    async getSession(sessionId: string): Promise<HitlSnapshot> {
      return core.request<HitlSnapshot>(`/hitl/sessions/${sessionId}`);
    },

    async sendMessage(sessionId: string, content: string, clientMessageId: string = createClientMessageId()): Promise<HitlMessage> {
      const res = await core.request<{ message: HitlMessage }>(`/hitl/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: {
          content: truncateUtf8(truncateCodePoints(content.trim(), HITL_MESSAGE_MAX_LENGTH), HITL_MESSAGE_MAX_BYTES),
          client_message_id: clientMessageId,
        },
      });
      return res.message;
    },

    async getReceipt(sessionId: string): Promise<Receipt> {
      return core.request<Receipt>(`/hitl/sessions/${sessionId}/receipt`);
    },

    async acknowledgeReceipt(sessionId: string): Promise<ReceiptAcknowledgement> {
      return core.request<ReceiptAcknowledgement>(`/hitl/sessions/${sessionId}/receipt/acknowledge`, {
        method: 'POST',
      });
    },
  };

  return api;
}

export type HitlModule = ReturnType<typeof createHitlModule>;

export function isHitlFeatureEnabled(value: string | undefined): boolean {
  return value === 'true';
}
